import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import Navbar from "../Components/Navbar";
import {
  useTicketDetailsQuery,
  useAdminReplayMutation,
  useCloseTicketMutation,
} from "../redux/features/user/userApi";
import { toast } from "react-toastify";

const TicketsD = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef(null);

  const {
    data: ticketData,
    isLoading,
    error,
    refetch,
  } = useTicketDetailsQuery(id, {
    skip: !id,
  });

  const [adminReply, { isLoading: isReplying }] = useAdminReplayMutation();
  const [closeTicket, { isLoading: isClosing }] = useCloseTicketMutation();

  // Extract ticket data and chat from API response
  const ticket = ticketData?.data?.ticketData || ticketData?.data?.ticket || ticketData?.data || null;
  const messages = ticketData?.data?.chat || ticket?.messages || ticket?.conversation || [];

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleReply = async () => {
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    try {
      await adminReply({
        ticket_id: id,
        message: message.trim(),
      }).unwrap();
      toast.success("Reply sent successfully");
      setMessage("");
      refetch();
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to send reply. Please try again."
      );
    }
  };

  const handleCloseTicket = async () => {
    if (
      !window.confirm(
        "Are you sure you want to close this ticket? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await closeTicket(id).unwrap();
      navigate("/tickets")
      toast.success("Ticket closed successfully");
      refetch();
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to close ticket. Please try again."
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const style = {
    height: 100,
    width: 100,
    margin: "auto",
    alignItem: "center",
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Lottie animationData={loader} style={style} loop={true} />
      </div>
    );
  }

  // Check if we have ticket data or chat data
  const hasData = ticketData?.data?.ticketData || ticketData?.data?.ticket || ticketData?.data;

  if (error || !hasData) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-semibold text-base sm:text-lg mb-4">
                {error?.data?.message ||
                  "Error loading ticket details. Please try again later."}
              </p>
              <button
                onClick={() => navigate("/tickets")}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
              >
                Back to Tickets
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Check status from ticketData or ticket
  const ticketStatus = ticket?.status ;

  console.log(`isClosed----138-----,TicketsD------>`, ticketStatus);

  const isClosed = ticketStatus
  const issueType =
    ticket?.issue_type ||
    ticket?.issueType ||
    ticket?.category ||
    ticketData?.data?.ticketData?.issue_type ||
    ticketData?.data?.ticketData?.issueType ||
    "General";

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="w-full border rounded-lg bg-white shadow-lg overflow-hidden">
            {/* Header */}
            <div className="p-4 sm:p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3">
                    <button
                      onClick={() => navigate("/tickets")}
                      className="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0 p-1"
                      aria-label="Back to tickets"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 sm:h-6 sm:w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 19l-7-7m0 0l7-7m-7 7h18"
                        />
                      </svg>
                    </button>
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 truncate">
                      Ticket Details
                    </h1>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs sm:text-sm text-gray-600 font-semibold">
                        Ticket ID:
                      </span>
                      <span className="font-mono text-blue-600 font-bold text-xs sm:text-sm truncate max-w-[150px] sm:max-w-none">
                        {ticket.ticket_id || ticket._id || ticket.ticketId || id}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs sm:text-sm text-gray-600 font-semibold">
                        Status:
                      </span>
                      <span
                        className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold ${isClosed
                          ? "bg-red-200 text-black-800"
                          : "bg-yellow-100 text-yellow-800"
                          }`}
                      >
                        {ticketStatus ? "Closed" : "Open"}
                      </span>
                    </div>
                  </div>
                </div>
                {!isClosed && (
                  <button
                    onClick={handleCloseTicket}
                    disabled={isClosing}
                    className="px-4 sm:px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm sm:text-base whitespace-nowrap"
                  >
                    {isClosing ? "Closing..." : "Close Ticket"}
                  </button>
                )}
              </div>
            </div>

            {/* Ticket Information */}
            <div className="p-4 sm:p-6 border-b bg-gray-50">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                Ticket Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-xs sm:text-sm text-gray-600 font-semibold">
                    Subject:
                  </span>
                  <p className="mt-1 text-gray-800 font-medium text-sm sm:text-base break-words">
                    {ticket.subject || ticket.title || "No Subject"}
                  </p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600 font-semibold">
                    Issue Type:
                  </span>
                  <p className="mt-1 text-gray-800 font-medium text-sm sm:text-base break-words">
                    {issueType}
                  </p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600 font-semibold">
                    User Email:
                  </span>
                  <p className="mt-1 text-gray-800 font-medium text-sm sm:text-base break-words">
                    {ticket.email || ticket.user?.email || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600 font-semibold">
                    Created At:
                  </span>
                  <p className="mt-1 text-gray-800 font-medium text-sm sm:text-base">
                    {formatDate(ticket.date)}
                  </p>
                </div>
                <div>
                  <span className="text-xs sm:text-sm text-gray-600 font-semibold">
                    Last Updated:
                  </span>
                  <p className="mt-1 text-gray-800 font-medium text-sm sm:text-base">
                    {formatDate(ticket.udate || ticket.updatedAt || ticket.lastUpdated)}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages/Conversation */}
            <div className="p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                Conversation
              </h2>
              <div className="border rounded-lg bg-gray-50 h-[300px] sm:h-[400px] md:h-[500px] overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8 text-sm sm:text-base">
                    No messages yet. Start the conversation by sending a reply.
                  </div>
                ) : (
                  messages.map((msg, index) => {
                    // Admin messages have avatar, user messages have email
                    const isAdmin = msg.avatar && msg.avatar.trim() !== "";
                    const isUser = msg.email && msg.email.trim() !== "";

                    return (
                      <div
                        key={index}
                        className={`flex ${isAdmin ? "justify-end" : "justify-start"} items-start gap-2 sm:gap-3`}
                      >
                        {/* Avatar for admin messages */}
                        {isAdmin && msg.avatar && (
                          <div className="flex-shrink-0">
                            <img
                              src={msg.avatar}
                              alt="Admin"
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-blue-600"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}

                        <div
                          className={`max-w-[85%] sm:max-w-[70%] rounded-lg p-3 sm:p-4 ${isAdmin
                            ? "bg-blue-600 text-white"
                            : "bg-white text-gray-800 border border-gray-200"
                            }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                            <span className="font-semibold text-xs sm:text-sm">
                              {isAdmin ? "Admin" : isUser ? msg.email || "User" : "User"}
                            </span>
                            {msg.date && (
                              <span
                                className={`text-xs ${isAdmin ? "text-blue-100" : "text-gray-500"
                                  }`}
                              >
                                {formatDate(msg.date)}
                              </span>
                            )}
                          </div>
                          {msg.message && (
                            <p className="text-xs sm:text-sm whitespace-pre-wrap break-words">
                              {msg.message}
                            </p>
                          )}
                          {/* File attachment */}
                          {msg.file && msg.file.trim() !== "" && (
                            <div className="mt-2">
                              <a
                                href={msg.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${isAdmin
                                  ? "bg-blue-700 text-white hover:bg-blue-800"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  }`}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                  />
                                </svg>
                                View Attachment
                              </a>
                            </div>
                          )}
                        </div>

                        {/* User avatar placeholder */}
                        {isUser && !isAdmin && (
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-300 flex items-center justify-center border-2 border-gray-400">
                              <span className="text-gray-600 text-xs sm:text-sm font-semibold">
                                {msg.email ? msg.email.charAt(0).toUpperCase() : "U"}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Reply Section */}
            {!isClosed && (
              <div className="p-4 sm:p-6 border-t bg-gray-50">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                  Reply to Ticket
                </h2>
                <div className="space-y-4">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your reply here..."
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg p-3 sm:p-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none text-sm sm:text-base"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.ctrlKey) {
                        handleReply();
                      }
                    }}
                  />
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* <p className="text-xs sm:text-sm text-gray-500">
                      Press Ctrl+Enter to send
                    </p> */}
                    <button
                      onClick={handleReply}
                      disabled={isReplying || !message.trim()}
                      className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm sm:text-base w-full sm:w-auto"
                    >
                      {isReplying ? "Sending..." : "Send Reply"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isClosed && (
              <div className="p-4 sm:p-6 border-t  bg-red-300 text-center">
                <p className=" text-gray-600 font-semibold text-sm sm:text-base">
                  This ticket is closed. No further replies can be sent.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketsD;
